import type {
  Chapter,
  ClassifiedChapterBeat,
  FourBeatChapterContract,
  GeneratedChapterBeat,
  InspectorAuthoritativeBeatId,
  InspectorBeatRepairRequest,
  InspectorVerdict,
  ValidationResult,
} from '../types';
import { FOUR_BEAT_SEPARATOR, assembleFourBeatChapter } from './fourBeatChapterAssembly.service';
import { classifySingleGeneratedBeat, toGeneratedChapterBeat } from './fourBeatChapterValidation.service';
import { recordInspectorLogicalCall } from './inspectorDecision.service';
import { FOUR_BEAT_AUTHORITATIVE_IDS } from './scribeThinkingPolicy.service';

/** Initial Inspector pass + one rerun after targeted beat repair. */
export const FOUR_BEAT_MAX_INSPECTOR_CALLS_PER_ATTEMPT = 2;

export interface FourBeatChapterSession {
  projectId: string;
  chapterNumber: number;
  runId: string;
  callBudget: FourBeatCallBudgetTracker;
  contract: FourBeatChapterContract;
  preInspectorRepairedBeatIds: string[];
  postInspectorRepairedBeatIds: string[];
  inspectorCallsUsed: number;
}

export function normalizeInspectorBeatRepairRequests(
  raw: unknown,
): { accepted: InspectorBeatRepairRequest[]; rejected: string[] } {
  if (!Array.isArray(raw)) {
    return { accepted: [], rejected: [] };
  }

  const authoritative = new Set<string>(FOUR_BEAT_AUTHORITATIVE_IDS);
  const seen = new Set<string>();
  const accepted: InspectorBeatRepairRequest[] = [];
  const rejected: string[] = [];

  for (const item of raw) {
    const beatId = typeof item?.beatId === 'string' ? item.beatId : '';
    if (!authoritative.has(beatId)) {
      if (beatId) rejected.push(beatId);
      continue;
    }
    if (seen.has(beatId)) {
      rejected.push(`${beatId}:duplicate`);
      continue;
    }
    seen.add(beatId);
    accepted.push({
      beatId: beatId as InspectorAuthoritativeBeatId,
      reasonCodes: Array.isArray(item?.reasonCodes)
        ? item.reasonCodes.map((code: unknown) => String(code)).filter(Boolean)
        : [],
      explanation: String(item?.explanation || '').trim(),
    });
  }

  return { accepted, rejected };
}

export function mapInspectorResponseToValidationResult(params: {
  verdict: InspectorVerdict;
  reason?: string;
  correction_order?: string;
  beatRepairRequests?: InspectorBeatRepairRequest[];
  rejectedBeatRepairIds?: string[];
  momentum_audit?: ValidationResult['momentum_audit'];
  momentum_correction?: ValidationResult['momentum_correction'];
  physicalStateChanges?: ValidationResult['physicalStateChanges'];
}): ValidationResult {
  const status = params.verdict === 'PASS' ? 'PASS' : 'FAIL';
  return {
    status,
    verdict: params.verdict,
    reason: params.reason,
    correction_order: params.correction_order,
    beatRepairRequests: params.beatRepairRequests,
    rejectedBeatRepairIds: params.rejectedBeatRepairIds,
    momentum_audit: params.momentum_audit,
    momentum_correction: params.momentum_correction,
    physicalStateChanges: params.physicalStateChanges,
  };
}

export function shouldAttemptInspectorBeatRepair(validation: ValidationResult): boolean {
  return (
    validation.verdict === 'REPAIR_REQUIRED' &&
    (validation.beatRepairRequests?.length ?? 0) > 0
  );
}

export function filterInspectorRepairCandidates(
  requests: InspectorBeatRepairRequest[],
  session: FourBeatChapterSession,
): { allowed: InspectorBeatRepairRequest[]; blockedBeatIds: string[] } {
  const repaired = new Set([
    ...session.preInspectorRepairedBeatIds,
    ...session.postInspectorRepairedBeatIds,
  ]);
  const allowed: InspectorBeatRepairRequest[] = [];
  const blockedBeatIds: string[] = [];

  for (const request of requests) {
    if (repaired.has(request.beatId)) {
      blockedBeatIds.push(request.beatId);
      continue;
    }
    allowed.push(request);
  }

  return { allowed, blockedBeatIds };
}

export function splitAssembledChapterToBeats(
  chapter: Chapter,
  contract: FourBeatChapterContract,
): GeneratedChapterBeat[] {
  const sections = (chapter.content || '').split(FOUR_BEAT_SEPARATOR);
  return contract.beats.map((contractBeat) =>
    toGeneratedChapterBeat({
      beatId: contractBeat.beatId,
      sequence: contractBeat.sequence,
      content: sections[contractBeat.sequence - 1] || '',
    }),
  );
}

export function replaceChapterBeat(
  chapter: Chapter,
  contract: FourBeatChapterContract,
  repairedBeat: GeneratedChapterBeat,
): Chapter {
  const beats = splitAssembledChapterToBeats(chapter, contract).map((beat) =>
    beat.beatId === repairedBeat.beatId ? repairedBeat : beat,
  );
  const generated = assembleFourBeatChapter({
    chapterId: contract.chapterNumber,
    title: chapter.title,
    summary: chapter.summary,
    beats,
    contract,
  });
  return {
    ...chapter,
    title: generated.title,
    summary: generated.summary,
    content: generated.content,
    wordCount: generated.totalWordCount,
  };
}

export function buildInspectorClassifiedBeat(
  beat: GeneratedChapterBeat,
  contractBeat: FourBeatChapterContract['beats'][number],
  repairRequest: InspectorBeatRepairRequest,
): ClassifiedChapterBeat {
  const classified = classifySingleGeneratedBeat(beat, contractBeat);
  return {
    ...classified,
    issues: [
      ...classified.issues,
      {
        beatId: beat.beatId,
        sequence: beat.sequence,
        code: 'POSSIBLE_SUMMARY_COMPRESSION',
        message: repairRequest.explanation || 'Inspector requested targeted beat repair.',
        severity: 'REPAIR',
      },
    ],
    status: 'REPAIR_REQUIRED',
  };
}

export interface InspectorOrchestrationResult {
  chapter: Chapter;
  validation: ValidationResult;
  inspectorCallsUsed: number;
  beatRepairAttempted: boolean;
  beatRepairSucceeded: boolean;
  repairedBeatIds: string[];
}

/**
 * App-equivalent Inspector follow-up: optional targeted beat repair, then one Inspector rerun.
 * Inspector calls do not consume four-beat Scribe logical-call budget.
 */
export async function runInspectorWithOptionalBeatRepair(params: {
  chapter: Chapter;
  initialValidation: ValidationResult;
  session: FourBeatChapterSession | null;
  inspectorCallsUsed: number;
  executeBeatRepair: (request: {
    chapter: Chapter;
    beatRepairRequest: InspectorBeatRepairRequest;
    session: FourBeatChapterSession;
  }) => Promise<{ repairedBeat: GeneratedChapterBeat }>;
  rerunInspector: (chapter: Chapter) => Promise<ValidationResult>;
}): Promise<InspectorOrchestrationResult> {
  let chapter = params.chapter;
  let validation = params.initialValidation;
  let inspectorCallsUsed = params.inspectorCallsUsed;
  let beatRepairAttempted = false;
  let beatRepairSucceeded = false;
  const repairedBeatIds: string[] = [];

  if (!params.session || !shouldAttemptInspectorBeatRepair(validation)) {
    return {
      chapter,
      validation,
      inspectorCallsUsed,
      beatRepairAttempted,
      beatRepairSucceeded,
      repairedBeatIds,
    };
  }

  const { allowed, blockedBeatIds } = filterInspectorRepairCandidates(
    validation.beatRepairRequests || [],
    params.session,
  );

  if (blockedBeatIds.length > 0) {
    console.warn(
      `[FOUR BEAT INSPECTOR REPAIR BLOCKED] ${JSON.stringify({ blockedBeatIds, reason: 'already_repaired_this_attempt' })}`,
    );
  }

  for (const repairRequest of allowed) {
    beatRepairAttempted = true;
    try {
      const { repairedBeat } = await params.executeBeatRepair({
        chapter,
        beatRepairRequest: repairRequest,
        session: params.session,
      });
      chapter = replaceChapterBeat(chapter, params.session.contract, repairedBeat);
      params.session.postInspectorRepairedBeatIds.push(repairRequest.beatId);
      repairedBeatIds.push(repairRequest.beatId);
      beatRepairSucceeded = true;
    } catch (error) {
      console.error(
        `[FOUR BEAT INSPECTOR REPAIR FAILED] ${JSON.stringify({ beatId: repairRequest.beatId, error: (error as Error).message })}`,
      );
      return {
        chapter,
        validation,
        inspectorCallsUsed,
        beatRepairAttempted,
        beatRepairSucceeded: false,
        repairedBeatIds,
      };
    }
  }

  if (beatRepairSucceeded && inspectorCallsUsed < FOUR_BEAT_MAX_INSPECTOR_CALLS_PER_ATTEMPT) {
    inspectorCallsUsed = recordInspectorLogicalCall(params.session, inspectorCallsUsed);
    validation = await params.rerunInspector(chapter);
  }

  return {
    chapter,
    validation,
    inspectorCallsUsed,
    beatRepairAttempted,
    beatRepairSucceeded,
    repairedBeatIds,
  };
}
